import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

import datamindsLogoWhite from '@/assets/login/dataminds-logo-white.svg';
import quoteMark from '@/assets/login/quote-mark.svg';
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem
} from '@/components/ui/carousel';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'Our team asks questions in plain language and gets answers straight from our own data. What used to take a week of analyst time now takes a conversation.',
    author: 'Mette Lindqvist',
    role: 'Head of Analytics, Nordlys Retail'
  },
  {
    quote:
      'Scheduled agents brief our leadership every Monday morning. Everyone starts the week looking at the same numbers, with the context already written up.',
    author: 'Jonas Kjær',
    role: 'COO, Havbro Logistics'
  },
  {
    quote:
      'Building agents for each department was easier than we expected. Finance, sales and support each have an assistant that knows their tools and their data.',
    author: 'Sofie Ahmadi',
    role: 'IT Director, Fjordvik Energi'
  }
];

const TESTIMONIAL_INTERVAL_MS = 6000;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function LoginHero() {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!api) return;

    const syncActiveIndex = () => setActiveIndex(api.selectedScrollSnap());
    syncActiveIndex();
    api.on('select', syncActiveIndex);
    return () => {
      api.off('select', syncActiveIndex);
    };
  }, [api]);

  useEffect(() => {
    if (!api || isPaused || TESTIMONIALS.length < 2) return;

    const timeout = setTimeout(
      () => api.scrollNext(prefersReducedMotion()),
      TESTIMONIAL_INTERVAL_MS
    );
    return () => clearTimeout(timeout);
  }, [api, activeIndex, isPaused]);

  return (
    <div className="login-hero-background flex h-full flex-col justify-between p-6">
      <img
        src={datamindsLogoWhite}
        alt="Dataminds"
        width={126}
        height={24}
      />
      <div
        className="relative overflow-hidden rounded-md bg-black/12"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        <Carousel
          setApi={setApi}
          opts={{ loop: true }}
          aria-label="Customer testimonials"
          className="cursor-grab select-none active:cursor-grabbing"
        >
          <CarouselContent className="ml-0">
            {TESTIMONIALS.map((item) => (
              <CarouselItem key={item.author} className="pl-0">
                <figure className="flex h-full flex-col justify-between gap-10 p-4">
                  <blockquote className="flex flex-col gap-4">
                    <img
                      src={quoteMark}
                      alt=""
                      width={20}
                      height={15}
                      draggable={false}
                    />
                    <p className="text-sm font-medium leading-5 text-neutral-50">
                      {item.quote}
                    </p>
                  </blockquote>
                  <figcaption className="flex min-w-0 flex-col gap-1 pr-24">
                    <span className="text-sm font-medium leading-5 text-white">
                      {item.author}
                    </span>
                    <span className="text-xs leading-4 text-neutral-50">
                      {item.role}
                    </span>
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="absolute bottom-4 right-4 flex h-10 items-center gap-3">
          {TESTIMONIALS.map((item, index) => (
            <button
              key={item.author}
              type="button"
              aria-label={item.author}
              aria-current={index === activeIndex}
              onClick={() => api?.scrollTo(index, prefersReducedMotion())}
              className={cn(
                'h-3 rounded-full transition-[width,background-color] motion-reduce:transition-none focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white',
                index === activeIndex
                  ? 'w-10 bg-neutral-50'
                  : 'w-3 bg-white/50 hover:bg-white/70'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
